import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LearningCard, LearningState, QualityRating } from '@/types';
import { calculateSM2, createInitialCard } from '@/lib/sm2';

interface LearningActions {
  initializeCard: (countryCode: string) => void;
  updateCard: (countryCode: string, quality: QualityRating) => void;
  getCard: (countryCode: string) => LearningCard | undefined;
  getDueCards: () => LearningCard[];
  getNextReviewCards: (
    count: number,
    availableCodes: string[]
  ) => LearningCard[];
  incrementSessionCount: () => void;
  reset: () => void;
}

type LearningStore = LearningState & LearningActions;

const initialState: LearningState = {
  cards: {},
  lastSessionDate: 0,
  totalSessionsCompleted: 0,
};

export const useLearningStore = create<LearningStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      initializeCard: (countryCode) => {
        if (get().cards[countryCode]) return;
        set((state) => ({
          cards: {
            ...state.cards,
            [countryCode]: createInitialCard(countryCode),
          },
        }));
      },

      updateCard: (countryCode, quality) => {
        const card = get().cards[countryCode];
        if (!card) return;

        const updated = calculateSM2(card, quality);
        set((state) => ({
          cards: { ...state.cards, [countryCode]: updated },
        }));
      },

      getCard: (countryCode) => get().cards[countryCode],

      getDueCards: () => {
        const now = Date.now();
        return Object.values(get().cards).filter(
          (card) => card.nextReviewDate <= now
        );
      },

      getNextReviewCards: (count, availableCodes) => {
        const { cards } = get();
        const now = Date.now();

        // Get all cards for available countries
        const availableCards = availableCodes
          .map((code) => cards[code])
          .filter((card): card is LearningCard => card !== undefined);

        // Sort by priority: due cards first, then by next review date
        return availableCards
          .sort((a, b) => {
            // Due cards come first
            const aIsDue = a.nextReviewDate <= now;
            const bIsDue = b.nextReviewDate <= now;
            if (aIsDue && !bIsDue) return -1;
            if (!aIsDue && bIsDue) return 1;

            // Then sort by next review date
            return a.nextReviewDate - b.nextReviewDate;
          })
          .slice(0, count);
      },

      incrementSessionCount: () => {
        set((state) => ({
          lastSessionDate: Date.now(),
          totalSessionsCompleted: state.totalSessionsCompleted + 1,
        }));
      },

      reset: () => set(initialState),
    }),
    {
      name: 'flag-trainer-learning',
      version: 1,
    }
  )
);
